import { ReactNode, useEffect, useRef, useState } from 'react'

type AutoFillGridProps<T> = {
	data: T[]
	renderCard: (item: T, index: number) => ReactNode
	renderPlaceholder?: (index: number) => ReactNode
	minCardWidth?: number
	gap?: number
}

/*
 * A specialized grid that adds the placeholder cards when there's less than 1 row.
 */
export function AutoFillGrid<T>({
	data,
	renderCard,
	renderPlaceholder,
	minCardWidth = 200,
	gap = 16,
}: AutoFillGridProps<T>) {
	const [columns, setColumns] = useState(1)
	const gridRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const updateColumns = () => {
			if (!gridRef.current) { return }

			// Get computed style to find the actual number of columns
			const computedStyle = window.getComputedStyle(gridRef.current)
			const templateColumns = computedStyle.gridTemplateColumns

			// Count the number of column definitions (space-separated values)
			const cols = templateColumns.split(' ').length

			setColumns(Math.max(1, cols))
		}

		// Need to wait for grid to render
		const timer = setTimeout(updateColumns, 0)

		window.addEventListener('resize', updateColumns)
		return () => {
			clearTimeout(timer)
			window.removeEventListener('resize', updateColumns)
		}
	}, [data.length])

	// Calculate how many placeholders we need
	// Only show placeholders if there's less than one full row
	const itemCount = data.length
	const remainder = itemCount % columns
	const placeholdersNeeded = renderPlaceholder && itemCount < columns && remainder !== 0 ? columns - remainder : 0

	return (
		<div
			ref={gridRef}
			style={{
				display: 'grid',
				gridTemplateColumns: `repeat(auto-fill, minmax(${minCardWidth}px, 1fr))`,
				gridAutoRows: '1fr',
				gap: `${gap}px`,
				width: '100%',
			}}
		>
			{data.map((item, index) => (
				<div key={`item-${index}`} data-type="item" style={{ display: 'flex' }}>
					{renderCard(item, index)}
				</div>
			))}
			{renderPlaceholder
				&& Array.from({ length: placeholdersNeeded }).map((_, index) => (
					<div key={`placeholder-${index}`} data-type="placeholder" style={{ display: 'flex' }}>
						{renderPlaceholder(index)}
					</div>
				))}
		</div>
	)
}
